import { useEffect, useState } from "react";
import { CancelablePromise } from "cancelable-promise";
import { RequestOptions } from "@hokuto/jam-core";
import { JamCanvasMapProps, JamParams, JamTileImageProps } from "../types.js";
import { MapApi } from "../api/map.js";
import { AtlasApi } from "../api/atlas.js";
import { useMonitor } from "../hooks/monitor.js";
import { useDrawerParams } from "../hooks/params.js";
import { useDrawer } from "../hooks/drawer.js";
import { CanvasDrawer } from "../utils/drawer.js";
import { getTilesFunctions } from "../utils/tiles.js";
import { ANIMATE, MIN_TILES, PROGRESSIVE } from "../const.js";

export function JamCanvasMap(args: Partial<JamCanvasMapProps>) {
  const props = useDrawer(args);
  if (!props) return null;
  return <JamCanvasMapRenderer {...props} />;
}

export function JamCanvasMapRenderer({
  tilesProps,
  canvasStatus,
  canvasDrawer,
  onError,
  showMonitor = false,
  options = {},
  drawersObservable,
  drawerIndex,
  controls,
  ...params
}: JamCanvasMapProps) {
  useMonitor({
    showMonitor,
    id: "monitor-map",
    canvasDrawer,
    top: "0",
    right: "0",
  });

  const invertedScale = canvasStatus.scale;

  const {
    progressive = PROGRESSIVE,
    animate = ANIMATE,
    minTiles = MIN_TILES,
    bgColor = canvasStatus.bgColor,
    ...props
  } = options;

  const { mapParams, atlasParams } = useDrawerParams({
    ...params,
    ...props,
    bgColor,
  });

  const { tileSize, w, h } = tilesProps;
  const tilesTotal = w * h;
  const { getTiles, getImages } = getTilesFunctions(tilesProps);

  const minCount = progressive
    ? Math.max(MIN_TILES, Math.min(minTiles, tilesTotal))
    : tilesTotal;

  const [currentTiles, setCurrentTiles] =
    useState<(() => CancelablePromise<JamTileImageProps>)[]>();
  const [modifier, setModifier] =
    useState<CancelablePromise<JamTileImageProps[]>>();

  const [canvasTiles, setCanvasTiles] = useState<JamTileImageProps[]>();

  const canvasAnimate = canvasTiles ? animate : false;

  function getTilesImages<T extends JamParams>(
    params: T,
    getImage: (
      args: T,
      body?: Partial<RequestOptions>
    ) => CancelablePromise<Blob>,
    body?: Partial<RequestOptions>
  ) {
    const tiles = getTiles();
    const images: JamTileImageProps[] = tiles.map(({ margin, ...tile }) => {
      const { w, h } = tile;
      const x = tileSize * w;
      const y = tileSize * h;
      const options = {
        ...params,
        margin,
      };

      return {
        ...tile,
        scale: invertedScale,
        x,
        y,
        getImage: () =>
          getImage(options, body).catch((e) => {
            const { cause = {} } = e;
            e.cause = {
              ...cause,
              canvasDrawer,
            };
            onError && onError(e);
            return Promise.reject(e);
          }),
      };
    });

    return getImages(images);
  }

  useEffect(() => {
    if (!mapParams) return;
    const { body } = mapParams;
    const currentTiles = getTilesImages(mapParams, MapApi.Image, body);
    setCurrentTiles(currentTiles);
  }, [mapParams]);

  useEffect(() => {
    if (!atlasParams) return;
    const { body } = atlasParams;
    const currentTiles = getTilesImages(atlasParams, AtlasApi.Image, body);
    setCurrentTiles(currentTiles);
  }, [atlasParams]);

  useEffect(() => {
    if (!currentTiles) return;

    const modifier = new CancelablePromise<JamTileImageProps[]>(
      (resolve, __, onCancel) => {
        onCancel(() => {
          currentTiles.forEach((promise) => promise().cancel());
        });

        const tilesN = !canvasTiles ? tilesTotal : minCount;
        const initialTiles = currentTiles.slice(0, tilesN);
        const otherTiles = currentTiles.slice(tilesN);

        const priorityTiles = Promise.all(
          initialTiles.map((item) => {
            return item().then((tile) => {
              return Promise.resolve(tile);
            });
          })
        ).then(([...tiles]) => {
          canvasDrawer.next({ ...canvasStatus, bgColor });
          CanvasDrawer.drawJamCanvas({
            reset: true,
            tiles,
            count: tiles.length,
            total: tilesTotal,
            canvasDrawer,
            animate: canvasAnimate,
          });

          setCanvasTiles(tiles);
          return Promise.resolve(tiles);
        });

        return priorityTiles
          .then(([...tiles]) => {
            return Promise.all(
              otherTiles.map((item) =>
                item().then((tile) => {
                  tiles.push(tile);
                  CanvasDrawer.drawJamCanvas({
                    tiles: [tile],
                    count: tiles.length,
                    total: tilesTotal,
                    canvasDrawer,
                    animate: canvasAnimate,
                  });

                  setCanvasTiles(tiles);
                  return Promise.resolve(tiles);
                })
              )
            ).then(() => Promise.resolve(tiles));
          })
          .then(resolve);
      }
    );
    setModifier(modifier);
  }, [currentTiles]);

  useEffect(() => {
    return () => {
      modifier && !modifier.isCanceled() && modifier.cancel();
    };
  }, [modifier]);

  return null;
}
